import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { userPreferencesService } from '../services/userPreferencesService';

type Theme = 'light' | 'dark';

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    syncThemeWithBarber: (barberId: string) => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setTheme] = useState<Theme>('light');
    const [currentBarberId, setCurrentBarberId] = useState<string | null>(null);

    // Initial load from localStorage for fast display
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme') as Theme;
        if (savedTheme) {
            setTheme(savedTheme);
            document.documentElement.classList.toggle('dark', savedTheme === 'dark');
        }
    }, []);

    // Sync theme with Firebase when barber logs in
    const syncThemeWithBarber = useCallback(async (barberId: string) => {
        if (!barberId || barberId === currentBarberId) return;

        setCurrentBarberId(barberId);
        try {
            const firebaseTheme = await userPreferencesService.getTheme(barberId);
            if (firebaseTheme && firebaseTheme !== 'system') {
                setTheme(firebaseTheme);
                localStorage.setItem('theme', firebaseTheme);
                document.documentElement.classList.toggle('dark', firebaseTheme === 'dark');
            }
        } catch (error) {
            console.error('[ThemeContext] Error syncing theme from Firebase:', error);
        }
    }, [currentBarberId]);

    const toggleTheme = useCallback(() => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
        document.documentElement.classList.toggle('dark', newTheme === 'dark');

        // Save to Firebase if barber is logged in
        if (currentBarberId) {
            userPreferencesService.saveTheme(currentBarberId, newTheme).catch(error => {
                console.error('[ThemeContext] Error saving theme to Firebase:', error);
            });
        }
    }, [theme, currentBarberId]);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, syncThemeWithBarber }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};